import { useEffect, useState, type ReactNode } from 'react';
import { ApiError, api, formatDateId } from '../api/client';
import type { ReceiptDetail } from '../api/types';
import { Alert, Button, Field, Modal, Spinner } from './ui';
import { useT } from '../i18n/locale';
import { RECEIPT_STATUS_LABEL, ReceiptStatus, VOID_ROLES } from '../api/enums';
import { useSession } from '../lib/session';

/**
 * Read-only view of one receipt.
 *
 * Exists mainly for the failed ones. A receipt that Accurate rejected shows as
 * "Gagal" in the list and nothing else, so the reason it was rejected — the
 * single most useful thing about it — was only visible in the server log. The
 * message is Accurate's own wording, shown verbatim rather than summarised,
 * because it is what the person will quote when they ask someone about it.
 */
export default function ReceiptDetailModal({
  receiptId,
  onClose,
  onVoided,
}: {
  receiptId: string;
  onClose: () => void;
  /** Lets the list reflect the new status without a manual refresh. */
  onVoided?: () => void;
}) {
  const t = useT();
  const { user } = useSession();
  const [data, setData] = useState<ReceiptDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * The void confirmation replaces this modal's body rather than opening a
   * second one on top. Stacked modals are hard to dismiss predictably, and the
   * reason being typed belongs with the receipt it is about.
   */
  const [confirmingVoid, setConfirmingVoid] = useState(false);
  const [reason, setReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  const canVoid =
    user !== null &&
    VOID_ROLES.includes(user.role) &&
    data?.receipt.status === ReceiptStatus.Posted;

  async function doVoid() {
    setVoiding(true);
    setVoidError(null);
    try {
      await api.post(`/receipts/${receiptId}/void`, { reason: reason.trim() });
      onVoided?.();
      onClose();
    } catch (e) {
      setVoidError(e instanceof ApiError ? e.message : t('detail.voidFailed'));
      setVoiding(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    api
      .get<ReceiptDetail>(`/receipts/${receiptId}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : t('detail.loadFailed'));
      });
    return () => {
      cancelled = true;
    };
  }, [receiptId]);

  const r = data?.receipt;

  return (
    <Modal
      open
      title={confirmingVoid ? t('detail.voidTitle') : t('detail.title')}
      onClose={onClose}
      width="max-w-3xl"
      dismissable={!voiding}
      footer={
        canVoid && !confirmingVoid ? (
          <Button variant="danger" onClick={() => setConfirmingVoid(true)}>
            {t('detail.voidAction')}
          </Button>
        ) : null
      }
    >
      {error ? <Alert tone="error">{error}</Alert> : null}

      {!data || !r ? (
        error ? null : (
          <Spinner label={t('detail.loading')} />
        )
      ) : confirmingVoid ? (
        <div className="flex flex-col gap-4">
          <Alert tone="warn">
            {t('detail.voidWarning', { number: r.grNumber, po: r.poNumber })}
          </Alert>

          <Field
            label={t('detail.voidReason')}
            hint={t('detail.voidReasonHint')}
          >
            <textarea
              autoFocus
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('detail.voidReasonPlaceholder')}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
          </Field>

          {voidError ? <Alert tone="error">{voidError}</Alert> : null}

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setConfirmingVoid(false);
                setVoidError(null);
              }}
              disabled={voiding}
            >
              {t('common.back')}
            </Button>
            {/* Disabled until the reason clears the server's own minimum, so
                the rule is not discovered by being rejected. */}
            <Button
              variant="danger"
              onClick={() => void doVoid()}
              disabled={voiding || reason.trim().length < 5}
            >
              {voiding ? t('detail.voiding') : t('detail.voidConfirm')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/*
            The reason comes first and only when there is one. Burying a
            rejection under eight rows of metadata would make the screen answer
            every question except the one that was asked.
          */}
          {r.status === ReceiptStatus.Failed && r.aolError ? (
            <Alert tone="error">
              <span className="block text-[11px] font-bold tracking-wider uppercase">
                {t('detail.failReason')}
              </span>
              <span className="mt-1 block break-words whitespace-pre-wrap">{r.aolError}</span>
            </Alert>
          ) : null}

          {r.status === ReceiptStatus.Failed && !r.aolError ? (
            <Alert tone="error">
              {t('detail.failNoReason')}
            </Alert>
          ) : null}

          {r.status === ReceiptStatus.Voided ? (
            <Alert tone="warn">
              <span className="block text-[11px] font-bold tracking-wider uppercase">
                {t('detail.voidedLabel')}
              </span>
              <span className="mt-1 block break-words whitespace-pre-wrap">
                {r.voidReason ?? t('detail.voidedNoReason')}
              </span>
              <span className="mt-1 block text-xs">
                {t('detail.voidedBy', { name: r.voidedByName ?? '-' })}
                {r.voidedAt ? ` · ${new Date(r.voidedAt).toLocaleString('id-ID')}` : ''}
              </span>
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            <Row label={t('detail.receiptNo')} value={r.grNumber} strong />
            {/* No fallback: RECEIPT_STATUS_LABEL is total over ReceiptStatus,
                so an unlabelled status is a compile error rather than a raw
                value leaking into the UI. */}
            <Row label={t('detail.status')} value={t(RECEIPT_STATUS_LABEL[r.status].key)} />
            <Row label={t('detail.date')} value={formatDateId(r.date)} />
            <Row label={t('detail.poNo')} value={r.poNumber} />
            <Row label={t('detail.vendor')} value={r.vendorName ?? '-'} />
            <Row label={t('detail.branch')} value={r.branchName ?? '-'} />
            <Row label={t('detail.warehouse')} value={r.warehouseName} />
            <Row label={t('detail.deliveryNote')} value={r.deliveryNoteNo || '-'} />
            <Row label={t('detail.database')} value={r.databaseAlias} />
            <Row label={t('detail.createdBy')} value={r.createdByName} />
            {/*
              Only when it differs from the GR number. The submit falls back to
              the number it sent when Accurate echoes nothing back, so showing
              both unconditionally would print the same string twice.
            */}
            {r.aolNumber && r.aolNumber !== r.grNumber ? (
              <Row label={t('detail.accurateNo')} value={r.aolNumber} />
            ) : null}
          </div>

          {r.notes ? (
            <div>
              <Label>{t('detail.notes')}</Label>
              <p className="m-0 text-sm break-words whitespace-pre-wrap text-slate-800">
                {r.notes}
              </p>
            </div>
          ) : null}

          <div>
            <Label>{t('detail.items', { count: data.lines.length })}</Label>
            <div className="mt-1 overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full border-collapse text-left">
                <thead className="bg-slate-100/60 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                  <tr>
                    <th className="px-2 py-1.5">{t('detail.colItem')}</th>
                    <th className="w-20 px-2 py-1.5 text-right">{t('detail.colOrdered')}</th>
                    <th className="w-20 px-2 py-1.5 text-right">{t('detail.colReceived')}</th>
                    <th className="w-24 px-2 py-1.5">{t('detail.colResult')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((l, i) => (
                    <tr key={`${l.sku}-${i}`} className="border-t border-slate-100">
                      <td className="px-2 py-1.5">
                        <span className="block text-xs font-bold text-slate-900">{l.itemName}</span>
                        <span className="block text-[11px] text-slate-500">
                          {[l.barcode, l.sku].filter(Boolean).join(' · ')}
                        </span>
                        {l.lineNotes ? (
                          <span className="mt-0.5 block text-[11px] break-words text-amber-700">
                            {l.lineNotes}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-2 py-1.5 text-right text-xs text-slate-600">
                        {l.qtyOrdered}
                      </td>
                      <td className="px-2 py-1.5 text-right text-xs font-bold text-slate-900">
                        {l.qtyReceived} <span className="font-normal text-slate-500">{l.unitName}</span>
                      </td>
                      <td className="px-2 py-1.5 text-[11px] font-semibold text-slate-700">
                        {/* Already Indonesian in the enum: 'Sesuai' / 'Kurang'. */}
                        {l.fulfilment}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Label({ children }: { children: ReactNode }) {
  return (
    <span className="block text-[11px] font-bold tracking-wider text-slate-500 uppercase">
      {children}
    </span>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="min-w-0">
      <Label>{label}</Label>
      <p
        className={`m-0 truncate text-sm text-slate-800 ${strong ? 'font-bold' : ''}`}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}
